import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toastSuccessOptions } from "../../../../App";
import { SelectedAccountContext } from "../../../../Context/SelectedAccountContext/SelectedAccountContext";
import { handleGraphQLErrors } from "../../../../utils/handleGraphQLError";
import { Icon } from "../../../Icon/Icon";
import { ApplicationsHeader } from "../../../SharedComponents/ApplicationDisplay/ApplicationDisplay";
import { EditableInformation } from "../../../SharedComponents/EditableInformation/EditableInformation";
import { Select, SelectOption } from "../../../SharedComponents/Select/Select";
import { ToggleButton } from "../../../SharedComponents/ToggleButton/ToggleButton";
import { NoUserApps } from "../../NoUserApps/NoUserApps";
import "./UserApps.css";

export const UserApps = ({ userId, userRoles }) => {
  const accessLevel = Cookies.get("access_level");
  const { t } = useTranslation();
  const {
    data: loggedInData,
    error: loggedInError,
    loading: loggedInLoading,
  } = useQuery(getUserQuery);
  const { selectedAccount } = useContext(SelectedAccountContext);
  const [workflowApps, setWorkflowApps] = useState([]);
  const [inventoryApps, setInventoryApps] = useState([]);
  const [canEditAccess, setCanEditAccess] = useState(false);
  const { data, loading, error } = useQuery(getUserApps, {
    variables: { userId },
  });

  const { data: allApps } = useQuery(getAccountApps, {
    variables: { accountIds: [selectedAccount?.id] },
  });

  const [updateAppRole, { loading: mutating }] = useMutation(
    updateSSOUserAppRoleMutation
  );

  const [toggleAccess, { loading: mutatingAccess }] = useMutation(
    toggleAppAccessMutation
  );

  useEffect(() => {
    if (data?.apps && allApps?.apps) {
      if (allApps.apps.length > 0) {
        const workflowAppsOnAccount = allApps.apps.filter(
          (x) => x.type === "WORKFLOW"
        );
        const inventoryAppsOnAccount = allApps.apps.filter(
          (x) => x.type === "INVENTORY"
        );

        setWorkflowApps(workflowAppsOnAccount);
        setInventoryApps(inventoryAppsOnAccount);
      }
    }
  }, [data, allApps]);

  useEffect(() => {
    if (accessLevel === "DISTRIBUTOR" || accessLevel === "SUPERADMIN") {
      setCanEditAccess(true);
      return;
    }

    if (loggedInData?.user?.roles?.sso && selectedAccount) {
      const roleOnAccount = loggedInData?.user?.roles?.sso.find((x) => {
        return x?.accountId === selectedAccount?.id;
      });

      if (roleOnAccount?.role === "ADMIN") {
        setCanEditAccess(true);
        return;
      }
    }
  }, [loggedInData, selectedAccount, accessLevel]);

  const updateRoleOnAppHandler = async (appId, newRole) => {
    try {
      await updateAppRole({
        variables: { appId, userId, role: newRole },
      });
      toast(t("toasts.roleChanged"), toastSuccessOptions);
    } catch (err) {
      handleGraphQLErrors(err);
    }
  };

  const toggleUserAccess = (appId, appType) => {
    const accountId = selectedAccount?.id;
    // userId

    toggleAccess({
      variables: { userId, accountId, appId, appType },
    })
      .then((data) => {
        toast(t("toasts.accessToggled"), toastSuccessOptions);
      })
      .catch((err) => {
        handleGraphQLErrors(err);
      });
  };

  useEffect(() => {
    if (error) {
      handleGraphQLErrors(error);
    }
    if (loggedInError) {
      handleGraphQLErrors(loggedInError);
    }
  });

  return (
    <div className="UserApps">
      <EditableInformation title="Applications">
        {(loading || loggedInLoading) && <div>Loading...</div>}
        {!loading && workflowApps.length > 0 && (
          <ApplicationsHeader app="WORKFLOW">
            {workflowApps.map((app) => {
              return (
                <UserAppsApp
                  app={app}
                  key={app.id}
                  role={userRoles.workflow.find(
                    (x) => x.applicationId === app.id
                  )}
                  setRole={updateRoleOnAppHandler}
                  isDisabled={mutating}
                  canEditAccess={canEditAccess}
                  toggleUserAccess={toggleUserAccess}
                  mutatingAccess={mutatingAccess}
                />
              );
            })}
          </ApplicationsHeader>
        )}
        {!loading && inventoryApps.length > 0 && (
          <ApplicationsHeader app="INVENTORY">
            {inventoryApps.map((app) => (
              <UserAppsApp
                app={app}
                key={app.id}
                role={userRoles.inventory.find(
                  (x) => x.applicationId === app.id
                )}
                setRole={updateRoleOnAppHandler}
                isDisabled={mutating}
                canEditAccess={canEditAccess}
                toggleUserAccess={toggleUserAccess}
                mutatingAccess={mutatingAccess}
              />
            ))}
          </ApplicationsHeader>
        )}
        {!loading &&
          inventoryApps.length === 0 &&
          workflowApps.length === 0 && <NoUserApps />}
        {loading && (
          <div
            style={{
              height: "52px",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading...
          </div>
        )}
      </EditableInformation>
    </div>
  );
};

const getUserApps = gql`
  query apps($userId: String) {
    apps(userId: $userId) {
      id
      name
      type
      accountId
    }
  }
`;

const getAccountApps = gql`
  query apps($accountIds: [String]) {
    apps(accountIds: $accountIds) {
      id
      name
      type
      accountId
    }
  }
`;
const getUserQuery = gql`
  query user {
    user {
      id
      name
      roles
    }
  }
`;

const updateSSOUserAppRoleMutation = gql`
  mutation updateSSOUserAppRole(
    $appId: String
    $userId: String
    $role: String
  ) {
    updateSSOUserAppRole(appId: $appId, userId: $userId, role: $role)
  }
`;

const UserAppsApp = ({
  app,
  role,
  setRole,
  isDisabled,
  canEditAccess,
  toggleUserAccess,
  mutatingAccess,
}) => {
  const { t } = useTranslation();
  const getIndicatorColor = () => {
    switch (app.type) {
      case "INVENTORY":
        return "#F4BD48";

      case "WORKFLOW":
        return "#008AC6";

      default:
        return "#008AC6";
    }
  };

  return (
    <div className="UserAppsApp">
      <div
        className="UserAppsApp__indicator"
        style={{ backgroundColor: getIndicatorColor() }}
      ></div>
      <div className="UserAppsApp__icon">
        <Icon name="menuAccounts" width={14} height={20} />
      </div>
      <div className="UserAppsApp__title">{app.name}</div>
      <div className="UserAppsApp__select">
        <Select
          change={(role) => setRole(app.id, role)}
          value={role?.role || "EMPLOYEE"}
          type="default"
          disabled={!canEditAccess || !role?.role}
        >
          {selectOptions.map((s) => (
            <SelectOption value={s.value} key={s.value}>
              {t(s.label)}
            </SelectOption>
          ))}
        </Select>
      </div>

      <div className="UserAppsApp__toggle">
        <ToggleButton
          toggled={role?.role ? true : false}
          canEditAccess={canEditAccess}
          toggle={() => {
            if (canEditAccess) {
              toggleUserAccess(app.id, app.type);
            }
          }}
        />
      </div>
    </div>
  );
};

const selectOptions = [
  {
    label: "Admin",
    value: "ADMIN",
  },
  {
    label: "Employee",
    value: "EMPLOYEE",
  },
];

const toggleAppAccessMutation = gql`
  mutation toggleAppAccess(
    $userId: String
    $accountId: String
    $appId: String
    $appType: String
  ) {
    toggleAppAccess(
      userId: $userId
      accountId: $accountId
      appId: $appId
      appType: $appType
    ) {
      id
      name
      email
      roles
    }
  }
`;
